export interface QuoteDraftInput {
  readonly studioName: string;
  readonly professionalName: string;
  readonly senderVatNumber?: string;
  readonly senderAddress?: string;
  readonly clientName: string;
  readonly clientVatNumber?: string;
  readonly clientAddress: string;
  readonly projectSubject: string;
  readonly projectLocation: string;
  readonly issueDate: string;
  readonly validityDays: string;
  readonly paymentTerms: string;
  readonly notes: string;
}

export interface QuoteDraft extends Omit<
  QuoteDraftInput,
  "validityDays" | "senderVatNumber" | "senderAddress" | "clientVatNumber"
> {
  readonly senderVatNumber: string;
  readonly senderAddress: string;
  readonly clientVatNumber: string;
  readonly validityDays: number;
  readonly expiryDate: string;
}

const FIELD_LIMITS = {
  studioName: 120,
  professionalName: 120,
  senderVatNumber: 32,
  senderAddress: 300,
  clientName: 160,
  clientVatNumber: 32,
  clientAddress: 300,
  projectSubject: 240,
  projectLocation: 240,
  paymentTerms: 600,
  notes: 1200,
} as const;

const FIELD_LABELS: Readonly<Record<keyof typeof FIELD_LIMITS, string>> = {
  studioName: "nome dello studio",
  professionalName: "nome del professionista",
  senderVatNumber: "partita IVA del mittente",
  senderAddress: "indirizzo del mittente",
  clientName: "destinatario",
  clientVatNumber: "partita IVA del destinatario",
  clientAddress: "indirizzo",
  projectSubject: "oggetto dell’incarico",
  projectLocation: "luogo dell’opera",
  paymentTerms: "condizioni di pagamento",
  notes: "note",
};

export const QUOTE_DRAFT_DATA_CLASSIFICATION = "contains_personal_data" as const;

function normalizeText(value: string): string {
  return value
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
    .trim();
}

function isIsoCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function addCalendarDays(value: string, days: number): string {
  const date = new Date(`${value}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function validateQuoteDraft(input: QuoteDraftInput): readonly string[] {
  const normalized = {
    studioName: normalizeText(input.studioName),
    professionalName: normalizeText(input.professionalName),
    senderVatNumber: normalizeText(input.senderVatNumber ?? ""),
    senderAddress: normalizeText(input.senderAddress ?? ""),
    clientName: normalizeText(input.clientName),
    clientVatNumber: normalizeText(input.clientVatNumber ?? ""),
    clientAddress: normalizeText(input.clientAddress),
    projectSubject: normalizeText(input.projectSubject),
    projectLocation: normalizeText(input.projectLocation),
    paymentTerms: normalizeText(input.paymentTerms),
    notes: normalizeText(input.notes),
  };
  const issues: string[] = [];

  if (normalized.studioName.length === 0) issues.push("Inserisci il nome dello studio.");
  if (normalized.professionalName.length === 0) issues.push("Inserisci il nome del professionista.");
  if (normalized.clientName.length === 0) issues.push("Inserisci il destinatario del preventivo.");
  if (normalized.projectSubject.length === 0) issues.push("Inserisci l’oggetto dell’incarico.");
  if (!isIsoCalendarDate(input.issueDate)) issues.push("Seleziona una data di emissione valida.");

  for (const [field, limit] of Object.entries(FIELD_LIMITS)) {
    const fieldName = field as keyof typeof FIELD_LIMITS;
    const value = normalized[fieldName];
    if (value.length > limit) {
      issues.push(`Il campo ${FIELD_LABELS[fieldName]} supera il limite di ${limit} caratteri.`);
    }
  }

  if (!/^\d+$/.test(input.validityDays)) {
    issues.push("La validità deve essere espressa in giorni interi.");
  } else {
    const validityDays = Number(input.validityDays);
    if (validityDays < 1 || validityDays > 365) {
      issues.push("La validità deve essere compresa tra 1 e 365 giorni.");
    }
  }

  return issues;
}

export function normalizeQuoteDraft(input: QuoteDraftInput): QuoteDraft {
  const issues = validateQuoteDraft(input);
  if (issues.length > 0) throw new Error(issues[0]);

  const validityDays = Number(input.validityDays);
  return Object.freeze({
    studioName: normalizeText(input.studioName),
    professionalName: normalizeText(input.professionalName),
    senderVatNumber: normalizeText(input.senderVatNumber ?? ""),
    senderAddress: normalizeText(input.senderAddress ?? ""),
    clientName: normalizeText(input.clientName),
    clientVatNumber: normalizeText(input.clientVatNumber ?? ""),
    clientAddress: normalizeText(input.clientAddress),
    projectSubject: normalizeText(input.projectSubject),
    projectLocation: normalizeText(input.projectLocation),
    issueDate: input.issueDate,
    validityDays,
    expiryDate: addCalendarDays(input.issueDate, validityDays),
    paymentTerms: normalizeText(input.paymentTerms),
    notes: normalizeText(input.notes),
  });
}
